import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';

import DataSource from '../../../../api';
import DataState from '../../../../Types/DataState';
import { Profile } from '../../../../Types/Profile';
import { User } from '../../../../Types/User';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import useCustomTracking from '../../../../Util/useCustomTracking';
import DisplayTrend from './DisplayTrend';
import {
  COMMON_USAGE_MAP,
  EmptyLighthouse,
  isQCSRelatedVertical,
} from './helper';
import DisplaySubMetrics from './Submetrics/DisplaySubMetrics';
import DisplayHeader from './Submetrics/DisplaySubMetricsHeader';
import { GeneralMetricType, LighthouseMetrics, TimeOption } from './types';
import ContextRender from './ContextWrapper';
import { convertToVerticalOption } from '../../../../Util/Vertical';
import styles from './MetricsCard.module.css';
import { renderUrl, urls } from '../../../../Util';
import PerformancePortalContext from '../../../PerformancePortalContext';
import useInspireOptions from '../../../../hooks/useInspireOptions';
import { isOmViewingTheirOwnProfile } from '../../helper';

const TRAILING_DAYS = 'Trailing 7 days';

interface MetricsCardProps {
  user: User;
  type: string;
  metrics: GeneralMetricType;
  displaySubtitles: string;
  isProfileTm: boolean;
  expandAll: boolean;
  dispatch: React.Dispatch<any>;
  vertical: string;
  channel: string;
  skill: string;
  segment: string;
  marketplace: string;
  week: string;
  profile: Profile;
  isHitByExpandAllButton: boolean;
  subMetrics: LighthouseMetrics;
  subMetricsState: string;
  cardState: any;
  setExpandAll: Function;
  cscEntityPrefix: string;
  cardWidth: string;
  displayScoreHeader: string;
  displayScoreFooter: string;
  responsiveFont: boolean;
  feedbackMsgText?: string;
  ccxQueryParam?: string;
}

interface ExpandableMetricsProps {
  user: User;
  type: string;
  open: boolean;
  setOpen: Function;
  isProfileTm: boolean;
  dispatch: React.Dispatch<any>;
  vertical: string;
  channel: string;
  skill: string;
  segment: string;
  marketplace: string;
  week: string;
  profile: Profile;
  isHitByExpandAllButton: boolean;
  submetrics: LighthouseMetrics;
  submetricsState: string;
  cscEntityPrefix: string;
  toplevelMetrics: GeneralMetricType;
  displayScoreHeader: string;
  displayScoreFooter: string;
  displaySubtitles: string;
  responsiveFont: boolean;
  ccxQueryParam?: string;
}

const ExpandableMetrics: React.FC<ExpandableMetricsProps> = ({
  user,
  type,
  open,
  setOpen,
  isProfileTm,
  dispatch,
  vertical,
  channel,
  skill,
  segment,
  marketplace,
  week,
  profile,
  isHitByExpandAllButton,
  submetrics,
  submetricsState,
  cscEntityPrefix,
  toplevelMetrics,
  displayScoreHeader,
  displayScoreFooter,
  responsiveFont,
  displaySubtitles,
  ccxQueryParam,
}: ExpandableMetricsProps): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const { isExclusivelyOm, isOm, userID } = useInspireOptions();

  const isVerticalBelongsToQCS = isQCSRelatedVertical(vertical);

  const [submetric, setSubmetric] =
    useState<LighthouseMetrics>(EmptyLighthouse);
  const [submetricStatus, setSubmetricStatus] = useState(DataState.LOADING);

  useEffect(() => {
    if (submetricsState === DataState.LOADED) {
      setSubmetric(submetrics);
      setSubmetricStatus(DataState.LOADED);
    } else if (submetricsState === DataState.ERROR) {
      setSubmetric(EmptyLighthouse);
      setSubmetricStatus(DataState.ERROR);
    } else {
      setSubmetric(EmptyLighthouse);
      setSubmetricStatus(DataState.LOADING);
    }
  }, [submetricsState]);

  useEffect(() => {
    if (!open || isHitByExpandAllButton) return;
    //Only Trigger Query when open status change and it is open
    const queryVariables = apiNaws.prepareLighthouseQuery(
      user.userID,
      cscEntityPrefix,
      isProfileTm,
      convertToVerticalOption(vertical),
      channel,
      skill,
      segment,
      marketplace,
      week,
      week === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
      [type],
      isProfileTm ? profile.directReports : []
    );

    apiNaws.getLighthouseMetrics(
      queryVariables,
      setSubmetric,
      setSubmetricStatus
    );
  }, [open]);

  const [ShowLessCloseTrackingTag, ShowLessCloseTrackClick] = useCustomTracking(
    `lighthouse-metric-${type}-show-less-link-slot`,
    `lighthouse-metric-${type}-show-less-link`,
    {
      'data-csa-c-type': 'link',
      'data-csa-c-tag1': 'Dashboard',
      'data-csa-c-tag2': 'Metrics',
      'data-csa-c-tag3': type,

      'data-csa-c-tag4': null,
    }
  );

  const verticalCard = `${vertical
    .toUpperCase()
    .replace(' ', '_')}-${type.toUpperCase()}`;

  const [ccxDetailsEnabled] = useState(
    isFeatureEnabled(Feature.ENABLE_HISTORICAL_CCX_DETAIL)
  );

  return (
    <>
      <Box>
        {ccxDetailsEnabled &&
          type.includes('CCX') &&
          !(
            isExclusivelyOm &&
            isOmViewingTheirOwnProfile(isOm, userID, profile.user.userID)
          ) && (
            <CCXDetailsLink
              ccxQueryParam={ccxQueryParam}
              isProfileTm={isProfileTm}
              profile={profile}
              type={type}
              user={user}
            />
          )}
        {open ? (
          <>
            <Text type='b300'>
              <Row spacing='none' spacingInset='none' width='119px'>
                <Icon tokens={chevronDownSmallTokens} />
                <ShowLessCloseTrackingTag />
                <Row spacing='none' spacingInset='none 400'>
                  <Link
                    type='secondary'
                    onClick={() => {
                      setOpen(false);
                      ShowLessCloseTrackClick();
                      dispatch({
                        type: 'off',
                        verticalCard: verticalCard,
                      });
                    }}
                  >
                    <FormattedMessage
                      id='cs_perfportal_showLess'
                      defaultMessage='Show less'
                    />
                  </Link>
                </Row>
              </Row>
            </Text>
            {submetricStatus === DataState.LOADING && (
              <Row
                wrap='down'
                width='100%'
                widths='fill'
                alignmentVertical='top'
                spacingInset='400 400'
              >
                <div style={{ textAlign: 'center' }}>
                  <Loader type='circular' size='large' />
                </div>
              </Row>
            )}

            {submetricStatus === DataState.LOADED && submetric && (
              <DisplayTrend
                metrics={
                  submetric[
                    type as keyof LighthouseMetrics
                  ]! as GeneralMetricType
                }
                type={type}
                user={user}
                isProfileTm={isProfileTm}
              />
            )}

            {submetricStatus === DataState.LOADED && (
              <>
                <DisplayHeader
                  type={type}
                  submetric={
                    submetric[
                      type as keyof LighthouseMetrics
                    ] as GeneralMetricType
                  }
                />
                <Column
                  alignmentHorizontal='start'
                  spacingInset='400 none none'
                >
                  <DisplaySubMetrics
                    type={type}
                    metrics={submetric!}
                    vertical={vertical}
                    cscEntityPrefix={cscEntityPrefix}
                    skill={skill}
                    channel={channel}
                    segment={segment}
                    marketplace={marketplace}
                    timeOption={
                      week === TRAILING_DAYS
                        ? TimeOption.DAILY
                        : TimeOption.WEEKLY
                    }
                    timestamp={
                      week === TRAILING_DAYS
                        ? moment(moment.now()).format('YYYY-MM-DD')
                        : week
                    }
                  />
                </Column>
              </>
            )}
            {submetricStatus === DataState.ERROR && (
              <Alert type='error'>
                <FormattedMessage
                  id='cs_perfportal_error_refresh_page'
                  defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                />
              </Alert>
            )}
          </>
        ) : (
          <Text type='b300'>
            <Row spacing='none 400' spacingInset='none' width='119px'>
              <Icon tokens={chevronRightSmallTokens} />
              <Link
                type='secondary'
                onClick={() => {
                  setOpen(true);
                  dispatch({
                    type: 'on',
                    verticalCard: verticalCard,
                  });
                }}
                data-csa-c-type='link'
                data-csa-c-slot-id={`lighthouse-metric-${type}-show-more-slot`}
                data-csa-c-content-id={`lighthouse-metric-${type}-show-more-link`}
                data-csa-c-description='Show More to get detailed sub-metrics'
                data-csa-c-tag1='Dashboard'
                data-csa-c-tag2='Metrics'
                data-csa-c-tag3={type}
                data-csa-c-tag4={null}
              >
                <FormattedMessage
                  id={COMMON_USAGE_MAP.showMore.id}
                  defaultMessage={COMMON_USAGE_MAP.showMore.defaultMessage}
                />
              </Link>
            </Row>
          </Text>
        )}
      </Box>
    </>
  );
};

const MetricsCard: React.FC<MetricsCardProps> = ({
  user,
  type,
  metrics,
  displaySubtitles,
  isProfileTm,
  expandAll,
  dispatch,
  vertical,
  channel,
  skill,
  segment,
  marketplace,
  week,
  profile,
  isHitByExpandAllButton,
  subMetrics,
  subMetricsState,
  cscEntityPrefix,
  displayScoreHeader,
  displayScoreFooter,
  responsiveFont,
  feedbackMsgText,
  ccxQueryParam,
}: MetricsCardProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(expandAll);
  }, [expandAll]);

  return (
    <Card maxWidth='448px' onClick={() => {}}>
      <Box spacingInset='none' minHeight='181px'>
        <ContextRender
          type={type}
          toplevelMetrics={metrics}
          displayScoreHeader={displayScoreHeader}
          displayScoreFooter={displayScoreFooter}
          responsiveFont={responsiveFont}
          displaySubtitles={displaySubtitles}
          feedbackMsgText={feedbackMsgText}
        />
        <ExpandableMetrics
          type={type}
          user={user}
          open={open}
          setOpen={setOpen}
          isProfileTm={isProfileTm}
          dispatch={dispatch}
          vertical={vertical}
          channel={channel}
          skill={skill}
          segment={segment}
          marketplace={marketplace}
          week={week}
          profile={profile}
          isHitByExpandAllButton={isHitByExpandAllButton}
          submetrics={subMetrics}
          submetricsState={subMetricsState}
          cscEntityPrefix={cscEntityPrefix}
          displayScoreHeader={displayScoreHeader}
          displayScoreFooter={displayScoreFooter}
          responsiveFont={responsiveFont}
          displaySubtitles={displaySubtitles}
          toplevelMetrics={metrics}
          ccxQueryParam={ccxQueryParam}
        />
      </Box>
    </Card>
  );
};

interface CCXDetailsLinkProps {
  type: string;
  isProfileTm: boolean;
  user: User;
  profile: Profile;
  ccxQueryParam?: string;
}

const CCXDetailsLink = ({
  type,
  isProfileTm,
  user,
  profile,
  ccxQueryParam,
}: CCXDetailsLinkProps) => {
  const navigate = useNavigate();

  const {
    performancePortalContextValue: ctx,
    setPerformancePortalContextValue: setCtx,
  } = useContext(PerformancePortalContext);

  const isNegativeTwoBoxEnabled = isFeatureEnabled(Feature.CCX_BOTTOM_TWO_BOX);
  const isNegativeType = type.includes('NEG');

  let ratingFilter: string;
  if (isNegativeType) {
    if (isNegativeTwoBoxEnabled) {
      ratingFilter = '1,2';
    } else {
      ratingFilter = '1';
    }
  } else {
    ratingFilter = '4,5';
  }

  return (
    <div className={styles.seeCCXDetailsLink}>
      <Link
        type='secondary'
        data-csa-c-type='link'
        data-csa-c-content-id={
          type.includes('NEG')
            ? 'lighthouse-metric-ccx-bottom-open-CCXDetails'
            : 'lighthouse-metric-ccx-topTwo-open-CCXDetails'
        }
        data-csa-c-is-tm={isProfileTm}
        data-csa-c-slot-id={
          type.includes('NEG')
            ? 'lighthouse-metric-ccx-bottom-open-CCXDetails-slot'
            : 'lighthouse-metric-ccx-topTwo-open-CCXDetails-slot'
        }
        data-csa-c-profile-id={profile?.user?.userID ?? ''}
        data-csa-c-username={user?.userName ?? ''}
        data-csa-c-tag1='Dashboard'
        data-csa-c-tag2='Metrics'
        data-csa-c-tag3={
          type.includes('NEG') ? 'CCX Bottom Box' : 'CCX Top-Two Box'
        }
        onClick={() => {
          if (setCtx) {
            setCtx({ ...ctx, passedCCXUserID: user.userID });
          }
          navigate(
            renderUrl(
              `${urls.ccxDetails}/${user.userID}?rating=${ratingFilter}${ccxQueryParam}`
            )
          );
        }}
      >
        <FormattedMessage
          id='cs_perfportal_dashboard_metrics_card'
          defaultMessage='See CCX Details'
          description='Dashboard button'
        />
      </Link>
    </div>
  );
};

export default MetricsCard;
